<template>
  <div class="mb-md">
    <button
      @click="modalOpen = true"
      class="flex items-center gap-sm p-0 bg-transparent c-blue-400 b-0 hover:c-blue-700"
    >
      <SloIcon name="ArrowsRotate" />
      {{ 'Subscribe & Save' | t }}
      <span v-if="product?.subscriptionPercentageDiscount">
        ({{ product?.subscriptionPercentageDiscount }}% {{ 'savings' | t }})
      </span>
    </button>
    <transition name="fade-up">
      <SloModal v-if="modalOpen && authenticated" :maxWidth="500" :form="false" @dismiss="handleCloseModal">
        <template v-slot:title>
          {{ 'Subscription details' | t }}
        </template>
        <div v-if="productExistsInCartWithoutASubscription" class="px-sm py-md bg-gray-50 rounded-md">
          <p class="m-0">
            {{ 'Note: Adding this item with Subscribe & Save will replace the existing item in the cart.' | t }}
          </p>
        </div>
        <div v-if="product?.estimatedShipDate" class="c-green-400 fw-strong text-sm">
          {{ 'Estimated ship date' | t }}: {{ product?.estimatedShipDate }}
        </div>
        <div class="flex items-center flex-wrap mt-md">
          <p class="m-0 text-lg">
            <span class="text-lg fw-strong">{{ product?.subscriptionPrice?.formattedValue }}</span>
            {{ product?.uom }}
          </p>
          <span class="inline-block ml-sm text-lg fw-strong c-green-400">
            {{ product?.subscriptionPercentageDiscount }}% {{ 'savings' | t }}
          </span>
        </div>
        <div class="mt-md">
          <p class="m-0">Save {{ product?.subscriptionPercentageDiscount }}% now with Subscribe & Save.</p>
        </div>
        <div class="flex flex-col justify-between items-center gap-md mt-md sm:flex-row">
          <SloQuantityInput
            :quantity="selectedQuantity"
            :min="product?.minOrderQuantity"
            @updated="handleQuantityUpdate($event)"
          />
          <SloField class="w-100 m-0">
            <SloSelect
              v-model="selectedSubscriptionFrequency"
              :placeholder="'Select a subscription frequency' | t"
              name="subscription_frequency"
              class="m-0"
            >
              <option
                v-for="(subscriptionFrequency, index) in product?.subscriptionFrequencies"
                :key="`subscription-frequency-${index}`"
                v-html="subscriptionFrequency.name"
                :value="subscriptionFrequency.code"
              />
            </SloSelect>
          </SloField>
        </div>
        <SloMinOrderQuantityWarning :minOrderQuantity="product?.minOrderQuantity" />
        <div class="grid grid-cols-1 gap-md w-100 sm:grid-cols-2 mt-lg">
          <SloButton @click="handleCloseModal" fill="outline">
            {{ 'Discard Changes' | t }}
          </SloButton>
          <SloButton @click="handleAddToCart">
            <SloLoader :loading="loading" size="md" color="white" class="p-0">
              {{ cartEntry?.subscriptionFrequency ? 'Update Subscription' : 'Add to Cart' | t }}
            </SloLoader>
          </SloButton>
        </div>
      </SloModal>
      <SubscriptionUnauthorizedModal
        v-else-if="modalOpen && !authenticated"
        :product="product"
        @dismiss="handleCloseModal"
      />
    </transition>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import t from '../../main/i18n';
import {findCartEntry, handleApiErrors, tagManagerTrack} from '../../main/utils';
import SloIcon from '../../../lib/components/SloIcon.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloModal from '../../../lib/components/SloModal.vue';
import SloProductMeta from '../../../lib/components/SloProductMeta.vue';
import SloAddToCart from '../../../lib/components/SloAddToCart.vue';
import SloSelect from '../../../lib/components/SloSelect.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloNumberInput from '../../../lib/components/SloNumberInput.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloQuantityInput from '../../../lib/components/SloQuantityInput.vue';
import SloMinOrderQuantityWarning from '../../../lib/components/SloMinOrderQuantityWarning.vue';
import SubscriptionUnauthorizedModal from './SubscriptionUnauthorizedModal.vue';

export default {
  name: 'SubscriptionPLPAddToCartButton',
  components: {
    SloLoader,
    SloNumberInput,
    SloField,
    SloSelect,
    SloAddToCart,
    SloProductMeta,
    SloModal,
    SloButton,
    SloIcon,
    SloQuantityInput,
    SloMinOrderQuantityWarning,
    SubscriptionUnauthorizedModal
  },
  filters: {
    t
  },
  props: {
    product: {type: Object, required: true}
  },
  data() {
    return {
      loading: false,
      modalOpen: false,
      selectedQuantity: 1,
      selectedSubscriptionFrequency: null
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticated']),
    ...mapGetters('marketplace', ['cartEntries']),
    sku() {
      return this.product?.code;
    },
    cartEntry() {
      const cartEntry = findCartEntry(this.cartEntries, this.sku);
      if (cartEntry) {
        this.selectedQuantity = cartEntry.quantity;
        this.selectedSubscriptionFrequency =
          cartEntry.subscriptionFrequency ?? this.product?.subscriptionFrequencies?.[0]?.code;
        return cartEntry;
      }

      this.selectedQuantity = this.product?.minOrderQuantity;
      this.selectedSubscriptionFrequency = this.product?.subscriptionFrequencies?.[0]?.code;

      return false;
    },
    productExistsInCartWithoutASubscription() {
      return this.cartEntry && !this.cartEntry?.subscriptionFrequency;
    }
  },
  watch: {
    modalOpen(isOpen) {
      if (isOpen) {
        this.selectedQuantity = this.cartEntry?.quantity ?? this.product?.minOrderQuantity;
        this.selectedSubscriptionFrequency = this.cartEntry?.subscriptionFrequency ?? this.product?.subscriptionFrequencies?.[0]?.code;
      }
    }
  },
  methods: {
    ...mapActions('marketplace', ['addToCart']),
    handleQuantityUpdate(value) {
      this.selectedQuantity = value;
    },
    async handleAddToCart() {
      this.loading = true;

      const data = {
        sku: this.sku,
        quantity: this.selectedQuantity,
        frequency: this.selectedSubscriptionFrequency,
        entryNumber: this.cartEntry?.entryNumber ?? false
      };

      await this.addToCart(data)
        .then(() => {
          this.loading = false;
          this.modalOpen = false;

          const cartEntry = findCartEntry(this.cartEntries, this.sku);
          if (cartEntry) {
            tagManagerTrack('add_to_cart', {
              currency: this.cartResponse?.subTotal?.currencyIso,
              user: this.user?.accountNumber || 'guest',
              cartId: this.cartId,
              cartSubTotal: this.cartResponse?.subTotal?.value,
              subscription: true,
              items: [
                {
                  item_id: this.sku,
                  item_name: cartEntry?.product?.number,
                  item_category: cartEntry?.product?.type,
                  item_list_name: this.listName,
                  price: cartEntry?.product?.price?.value,
                  quantity: this.quantity,
                  totalPrice: cartEntry?.totalPrice?.value
                }
              ]
            });
          }
        })
        .catch(e => handleApiErrors(e, this));
    },
    handleCloseModal() {
      this.modalOpen = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.sub-and-save-quantity {
  flex: 1 0 25%;
  width: 100%;
}
</style>
